import { useMutation, useQueryClient } from '@tanstack/react-query';

import type { LinePayload } from '../common/payload.types';
import type { PaymentTerm } from '../common/types';

import { apiResourceSchema } from '../common/schemas';
import { fetchJson } from '../fetch';
import { QueryKeys } from '../keys';
import { submittedInvoiceSchema } from './schemas';
import { invoiceQueryOptions } from './useInvoice';

export type Payload = {
  approvalType: 'manual' | 'automatic';
  brandId: number;
  clientContactId: number;
  clientId: number;
  clientReference: string | null;
  conditionId: number | null;
  customAgreementId: number | null;
  customConditionId: number | null;
  description: string | null;
  fcEntryNumber: string | null; // Only nullable in edit mode
  invoiceLines: LinePayload[];
  nextNumber: number | null; // Only nullable in edit mode
  numberingSeriesId: number;
  paymentTerm: PaymentTerm;
  paymentTermEndOfMonth: boolean;
  specialConditions: string | null;
  title: string;
  userComments: string | null;
};

export const useSubmitInvoice = (id: number) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (body: Payload) => {
      const { data: invoice } = await fetchJson(`/invoices/${id}/submit`, {
        method: 'PATCH',
        schema: apiResourceSchema(submittedInvoiceSchema),
        body,
      });

      return invoice;
    },
    onSuccess: (updatedInvoice) => {
      queryClient.setQueryData(invoiceQueryOptions(updatedInvoice.id).queryKey, () => updatedInvoice);

      queryClient.invalidateQueries({ queryKey: QueryKeys.invoices.show.id(id) });
      queryClient.invalidateQueries({ queryKey: QueryKeys.invoices.index.root });
      queryClient.invalidateQueries({ queryKey: QueryKeys.invoices.creditable });
      queryClient.invalidateQueries({ queryKey: QueryKeys.statistics.root });
      queryClient.invalidateQueries({ queryKey: QueryKeys.user }); // Refetch numbering series to update the next number

      if (updatedInvoice.invoiceTemplateId !== null) {
        queryClient.invalidateQueries({ queryKey: QueryKeys.invoiceTemplates.index.root });
        queryClient.invalidateQueries({ queryKey: QueryKeys.invoiceTemplates.show.id(updatedInvoice.invoiceTemplateId) });
      }
    },
  });
};
